<script setup lang="ts">
import { ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { useLocale } from '@/composables/useLocale'
import LocaleFlag from '@/components/layout/LocaleFlag.vue'

const { t } = useI18n()
const { currentLocale, availableLocales, changeLocale } = useLocale()

const isOpen = ref(false)
const dropdown = ref<HTMLDivElement | null>(null)

function toggle() {
  isOpen.value = !isOpen.value
}

function close() {
  isOpen.value = false
}

async function selectLocale(code: string) {
  await changeLocale(code)
  close()
}

function onKeydown(e: KeyboardEvent) {
  if (e.key === 'Escape') close()
}

function currentNativeName() {
  return availableLocales.value.find((l) => l.code === currentLocale.value)?.native ?? currentLocale.value
}
</script>

<template>
  <div class="relative" @keydown="onKeydown">
    <button
      type="button"
      class="flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-lg text-muted hover:text-text dark:text-dark-muted dark:hover:text-dark-text hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
      :aria-label="t('nav.language')"
      :aria-expanded="isOpen"
      aria-haspopup="listbox"
      @click="toggle"
      @blur="close"
    >
      <LocaleFlag :locale="currentLocale" size="sm" />
      <span>{{ currentNativeName() }}</span>
      <svg class="w-3 h-3 transition-transform" :class="{ 'rotate-180': isOpen }" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
        <path stroke-linecap="round" stroke-linejoin="round" d="M19 9l-7 7-7-7" />
      </svg>
    </button>

    <Transition
      enter-active-class="transition-all duration-200 ease-out"
      enter-from-class="opacity-0 scale-95 -translate-y-1"
      enter-to-class="opacity-100 scale-100 translate-y-0"
      leave-active-class="transition-all duration-150 ease-in"
      leave-from-class="opacity-100 scale-100 translate-y-0"
      leave-to-class="opacity-0 scale-95 -translate-y-1"
    >
      <div
        v-if="isOpen"
        ref="dropdown"
        role="listbox"
        :aria-label="t('nav.language')"
        class="absolute top-full end-0 mt-2 min-w-[12.5rem] py-1 rounded-xl glass dark:glass-dark border border-slate-200/60 dark:border-slate-700/40 shadow-glass-lg z-50"
      >
        <button
          v-for="loc in availableLocales"
          :key="loc.code"
          role="option"
          type="button"
          :aria-selected="loc.code === currentLocale"
          class="w-full flex items-center justify-between gap-3 px-4 py-2.5 text-sm transition-colors"
          :class="[
            loc.code === currentLocale
              ? 'text-primary bg-primary-soft/50 dark:bg-primary/10 font-medium'
              : 'text-text dark:text-dark-text hover:bg-slate-100 dark:hover:bg-slate-800',
          ]"
          :dir="loc.rtl ? 'rtl' : 'ltr'"
          @mousedown.prevent="selectLocale(loc.code)"
        >
          <span class="flex min-w-0 items-center gap-2.5">
            <LocaleFlag :locale="loc.code" size="sm" />
            <span class="truncate">{{ loc.native }}</span>
          </span>
          <span class="shrink-0 text-xs text-muted">{{ loc.name }}</span>
        </button>
      </div>
    </Transition>
  </div>
</template>
